import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import PtfButton from "@/components/PtfButton";
import { MaskText, Reveal } from "@/components/motion/Reveal";
import { IMAGES } from "@/lib/content";

export default function Hero() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative min-h-[100svh] overflow-hidden bg-ptf-primary text-ptf-bg grain"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img
          src={IMAGES.hero}
          alt="Mist rising over an Indian mountain forest at dawn"
          className="h-full w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-ptf-primary" />
      <div className="absolute inset-0 bg-gradient-to-t from-ptf-primary via-transparent to-ptf-primary/40" />

      {/* Hero content: normal document flow — eyebrow → headline → description → CTAs.
          Entrance animations are opacity-only (no translate) so elements can never
          overlap, even mid-animation. pt guards against the fixed header on short
          viewports; justify-end anchors content to the lower half. */}
      <div className="relative z-10 min-h-[100svh] mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 flex flex-col justify-end pt-32 md:pt-36 pb-14 md:pb-24">
        <Reveal delay={0.1} y={0}>
          <p className="text-[0.7rem] sm:text-xs uppercase tracking-[0.3em] text-ptf-accent mb-3.5 md:mb-5 xl:mb-7 flex items-center gap-3">
            <span className="h-px w-10 bg-ptf-accent inline-block" />
            {t("home.hero_eyebrow", { defaultValue: "Based in India · Working Globally" })}
          </p>
        </Reveal>

        <MaskText
          animate
          tag="h1"
          delay={0.25}
          lines={t("home.hero_headline", { returnObjects: true, defaultValue: ["For a planet worth", "exploring.", "And protecting."] })}
          className="font-display font-extrabold uppercase tracking-tight max-w-[1200px] text-[clamp(2.25rem,9vw,3rem)] leading-[1.05] sm:text-6xl sm:leading-[1.0] lg:text-7xl lg:leading-[0.95]"
          lastLineClassName="text-ptf-accent"
        />

        <Reveal delay={0.75} y={0}>
          <p data-testid="hero-tagline" className="mt-5 md:mt-6 font-serif-ed italic text-lg sm:text-xl text-ptf-accent max-w-2xl">
            Empowering Communities. Conserving Nature. Creating Sustainable Impact.
          </p>
        </Reveal>

        <Reveal delay={0.9} y={0}>
          <p className="mt-5 md:mt-6 max-w-xl text-base sm:text-lg text-ptf-bg/80 leading-relaxed font-light">
            {t("home.hero_p", { defaultValue: "Conservation, responsible travel and community development, carried out as one body of work rather than three separate causes." })}
          </p>
        </Reveal>

        <Reveal delay={1.05} y={0}>
          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-4">
            <PtfButton to="/focus" variant="accent" data-testid="hero-explore-cta">{t("home.hero_cta_explore", { defaultValue: "Explore Our Work" })}</PtfButton>
            <PtfButton to="/partnerships" variant="outlineLight" data-testid="hero-partner-cta">{t("home.hero_cta_partner", { defaultValue: "Partner With Us" })}</PtfButton>
          </div>
        </Reveal>
      </div>

      <motion.div
        className="absolute bottom-6 right-6 z-10 hidden md:flex items-center gap-2 text-ptf-bg/60 text-[0.65rem] uppercase tracking-[0.24em]"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
      >
        {t("home.hero_scroll", { defaultValue: "Scroll" })} <ArrowDown className="h-4 w-4" />
      </motion.div>
    </section>
  );
}
